<script setup lang="ts">
import { onMounted, ref, Ref, watch } from 'vue'
import { TagSelector } from '@/composables/charts/influx-charts/query-builders/InfluxTypes'
import { useMeasurementStore } from '@/store/measurements'
import _ from 'lodash'

const model = defineModel<TagSelector<'label'>>({
  default: {
    tag: 'label',
    useAll: true,
    groupByValues: false,
    allowEmptyValues: false,
  },
})

const labels: Ref<string[]> = ref([])
const useAllLabels: Ref<boolean> = ref(true)
const groupByLabel: Ref<boolean> = ref(true)
const availableLabels: Ref<string[]> = ref([])

watch(
  model,
  () => {
    if (!model.value) return
    if (model.value.useAll !== useAllLabels.value) useAllLabels.value = useAllLabels.value
    if (model.value.groupByValues !== groupByLabel.value) groupByLabel.value = model.value.groupByValues
    if (!model.value.useAll && model.value.specificValues?.length) labels.value = model.value.specificValues
  },
  { immediate: true, deep: true }
)

watch(
  () => [labels, useAllLabels, groupByLabel],
  () => {
    const newModel: TagSelector<'label'> = {
      tag: 'label',
      useAll: useAllLabels.value,
      groupByValues: groupByLabel.value,
      allowEmptyValues: false, //should never happen anyway
    }
    if (!useAllLabels.value) newModel.specificValues = labels.value
    if (_.isEqual(model.value, newModel)) return
    model.value = newModel
  },
  { immediate: true, deep: true }
)

async function loadLabels() {
  availableLabels.value = await useMeasurementStore().loadUsedLabels()
}
onMounted(async () => {
  await loadLabels()
})
</script>

<template>
  <v-switch
    v-model="groupByLabel"
    :label="$t('chart_sharing.creator.customize.tag_selectors.label.group_by')"
    hide-details
  />
  <v-switch
    v-model="useAllLabels"
    :label="$t('chart_sharing.creator.customize.tag_selectors.label.use_all')"
    hide-details
  />
  <v-fade-transition>
    <v-autocomplete
      v-if="!useAllLabels"
      v-model="labels"
      prepend-icon="mdi-tag"
      variant="outlined"
      :label="$t('chart_sharing.creator.customize.tag_selectors.label.specific')"
      multiple
      :disabled="useAllLabels"
      :items="availableLabels"
      hide-details
    />
  </v-fade-transition>
</template>
